import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { LoadingController } from '@ionic/angular';
import { BehaviorSubject, combineLatest, Subject, takeUntil } from 'rxjs';
import { AppEventType } from '../enums/app-events.enum';
import { Alert, AlertType } from '../models/alert/alert';
import { DateTimeHelper, DateTimeType } from '../utils/date-time-helper';
import { AppUserClockStateService } from './app-user-clock-state.service';
import { AppUserDailyTimeStatusService } from './app-user-daily-time-status.service';
import { EventBusService } from './event-bus.service';
import { ProjectBoundaryTrackingService } from './project-boundary-tracking.service';

@Injectable({
  providedIn: 'root'
})
export class AlertService implements OnDestroy {

  private destroy$ = new Subject<void>();

  private shownAlertFlags = new Map<AlertType, boolean>();

  private alertsSubject = new BehaviorSubject<Alert[]>([]);
  alert$ = this.alertsSubject.asObservable();

  private alerts: Alert[] = [];

  private readonly outOfProjectBoundaryAlert: Alert = {
    name: AlertType.OUT_OF_BOUNDARY,
    header: 'Attention:',
    message:
      'Your last reported position was not within the project boundary. Please visit the project map to determine the boundary. ' +
      'You can click "Retry" to verify your current position.',
    buttons: [
      {
        label: 'Retry',
        action: async () => await this.refetchUserCurrentLocation()
      }
    ]
  };

  private readonly hasReturnTimeAlert: Alert = {
    name: AlertType.RETURNED_TIME,
    header: 'Attention:',
    message:
      'Your time has been returned by a supervisor and requires correction.',
    buttons: [
      {
        label: 'Go to Activity Log'
      }
    ]
  };

  private readonly unsubmittedTimeAlert: Alert = {
    name: AlertType.UNSUBMITTED_TIME,
    header: 'Reminder:',
    message:
      'You must sign and submit your Activity Log after clocking out for the day.',
    buttons: [
      {
        label: 'Go to Activity Log'
      }
    ]
  };

  constructor(
    private projectBoundaryTrackingService: ProjectBoundaryTrackingService, 
    private eventBusService: EventBusService, 
    private appUserDailyTimeStatusService: AppUserDailyTimeStatusService,
    private appUserClockStateService: AppUserClockStateService,  
    private router: Router, 
    private loadingController: LoadingController
  ) {
    this.loadSubscriptions();
  }

  loadSubscriptions(): void {
    this.projectBoundaryTrackingService.isInProjectBoundary$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isInBoundary) => {
          if (!isInBoundary && !this.hasShownAlert(AlertType.OUT_OF_BOUNDARY)) {
            this.setAlertFlag(AlertType.OUT_OF_BOUNDARY,true);
            this.alerts.push(this.outOfProjectBoundaryAlert);
            this.alertsSubject.next([...this.alerts]);
            this.projectBoundaryTrackingService.scheduleOutOfBoundaryNotification();
          } else if (isInBoundary && this.hasShownAlert(AlertType.OUT_OF_BOUNDARY)) {
            this.resetAlert(this.outOfProjectBoundaryAlert);
            this.projectBoundaryTrackingService.removeScheduledOutOfBoundaryNotification();
          }
        }
    );

    combineLatest([this.appUserDailyTimeStatusService.dailyTimeStatuses$, this.appUserClockStateService.isClockedOut$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([dateStatuses, isClockedOut]) => {
        // Handle returned time alerts
        const firstDateWithReturnedTime = dateStatuses.find(status => status.hasReturnedTime)?.date;
        if (firstDateWithReturnedTime && !this.hasShownAlert(AlertType.RETURNED_TIME)) {
          this.setAlertFlag(AlertType.RETURNED_TIME, true);
          this.hasReturnTimeAlert.buttons[0].action = async () => await this.navigateToActivityLog(firstDateWithReturnedTime);
          this.alerts.push(this.hasReturnTimeAlert);
          this.alertsSubject.next([...this.alerts]);
        } else if (!firstDateWithReturnedTime && this.hasShownAlert(AlertType.RETURNED_TIME)) {
          this.resetAlert(this.hasReturnTimeAlert);
        }

        // Handle unsubmitted time alerts
        const firstDateWithUnsubmittedTime = dateStatuses.find(status => status.hasUnsubmittedTime)?.date;
        const hasUnsubmitted = !!firstDateWithUnsubmittedTime;
        const unsubmittedDateisToday = DateTimeHelper.checkIsToday(firstDateWithUnsubmittedTime);
        const alreadyShown = this.hasShownAlert(AlertType.UNSUBMITTED_TIME);

        if (hasUnsubmitted && !alreadyShown && (!unsubmittedDateisToday || isClockedOut)) {
          this.setAlertFlag(AlertType.UNSUBMITTED_TIME, true);
          this.unsubmittedTimeAlert.buttons[0].action = async () => await this.navigateToActivityLog(firstDateWithUnsubmittedTime);
          this.alerts.push(this.unsubmittedTimeAlert);
          this.alertsSubject.next([...this.alerts]);
        } else if (alreadyShown && (!hasUnsubmitted || (unsubmittedDateisToday && !isClockedOut))) {
          this.resetAlert(this.unsubmittedTimeAlert);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private resetAlert(alert: Alert) {
    this.setAlertFlag(alert.name,false);
    const alertName = alert.name;
    this.alerts = this.alerts.filter(alert => alert.name !== alertName);
    this.alertsSubject.next([...this.alerts]);
  }

  private async refetchUserCurrentLocation(){
    // Show the loading spinner
    const loading = await this.loadingController.create({
      message: 'Verifying location...'
    });
    await loading.present();
    try{
      this.eventBusService.emit({
      type: AppEventType.RETRY_LOCATION_REQUESTED_BY_USER, data: null
      });
      }catch(exception){
        console.error('Location retry failed:', exception);
      }finally {
        await loading.dismiss();
      }
  }

  private async navigateToActivityLog(date: DateTimeType | null) {
     if (date) {
       const dateString = DateTimeHelper.formatDateTimeAsDate(date);
       await this.router.navigate(['app/activityLog'], { replaceUrl: true, queryParams: { date: dateString } });
     }
     else {
       await this.router.navigate(['app/activityLog'], { replaceUrl: true });
     }
   }

   private hasShownAlert(type: AlertType): boolean {
    if (!this.shownAlertFlags.has(type)) {
      this.shownAlertFlags.set(type, false); 
    }
    return this.shownAlertFlags.get(type);
  }

  private setAlertFlag(type: AlertType, value: boolean): void {
     if (this.shownAlertFlags.has(type)) {
      this.shownAlertFlags.set(type, value);
    }
  }
}
